import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, Cell } from "recharts";
import { api, errMsg } from "../lib/api";
import { Kpi, PageHeader, STATUS_COLORS, BRANCH_LABEL } from "../components/common";
import { toast } from "sonner";
import { HotLeads } from "../components/HotLeads";

const tip = { contentStyle: { background: "#ffffff", border: "1px solid #e3e7ef", borderRadius: 10, fontSize: 12 }, cursor: { fill: "#f1f4f9" } };
const TICK = { fill: "#9aa5b8", fontSize: 11 };
const SOURCE_MARGIN = { left: 30 };

const Filters = ({ f, setF, staff }) => (
  <div className="ax-panel p-3 grid grid-cols-2 md:grid-cols-6 gap-2 mb-6" data-testid="dashboard-filters">
    {[["date_from", "From", "date"], ["date_to", "To", "date"]].map(([k, l, t]) => (
      <label key={k} className="text-xs"><span className="ax-label">{l}</span>
        <input data-testid={`dash-filter-${k}`} type={t} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1 w-full bg-muted/60 border h-8 px-2 text-sm" /></label>
    ))}
    {[["branch", "Branch", [["", "All accessible"], ["DEL", "Delhi (FBD)"], ["MUM", "Mumbai"], ["UNASSIGNED", "Unassigned"]]],
      ["owner", "Owner", [["", "All"], ...staff.map((s) => [s.v, s.t])]],
      ["type", "Segment", [["", "All"], ["Corporate", "Corporate"], ["SMB", "SMB"], ["D2C", "D2C"], ["Agency", "Agency"]]],
      ["customer_type", "New / Old", [["", "All"], ["New", "New"], ["Old", "Old (repeat)"]]]].map(([k, l, opts]) => (
      <label key={k} className="text-xs"><span className="ax-label">{l}</span>
        <select data-testid={`dash-filter-${k}`} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1 w-full bg-muted/60 border h-8 px-2 text-sm">
          {opts.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
        </select></label>
    ))}
  </div>
);

const Panel = ({ title, children, testid, className = "" }) => (
  <div className={`ax-panel p-4 ${className}`} data-testid={testid}><div className="ax-label mb-3">{title}</div>{children}</div>
);

export default function SalesDashboard() {
  const [f, setF] = useState({});
  const [d, setD] = useState(null);
  const [staff, setStaff] = useState([]);
  const [err, setErr] = useState(null);
  const nav = useNavigate();
  useEffect(() => { api.get("/staff").then((r) => setStaff(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    api.get("/sales/dashboard", { params }).then((r) => setD(r.data)).catch((e) => { setErr(errMsg(e)); toast.error(errMsg(e)); });
  }, [f]);
  const monthly = useMemo(() => (d ? d.monthly.map((m) => ({ m: m._id, n: m.n, won: m.won })) : []), [d]);
  const bySource = useMemo(() => (d ? d.new_by_source.map((s) => ({ s: s._id || "(none)", n: s.n })) : []), [d]);
  if (err && !d) return <div className="ax-panel p-8 text-sm" data-testid="dashboard-no-access">{err}</div>;
  if (!d) return <div className="text-sm text-muted-foreground" data-testid="dashboard-loading">Loading dashboard…</div>;
  const lbl = Object.fromEntries(d.statuses.map((s) => [s.code, s.label]));
  const fu = d.followups || {};
  const funnel = d.statuses.map((s) => ({ code: s.code, name: s.label, n: d.funnel.find((x) => x._id === s.code)?.n || 0 }));
  return (
    <div>
      <PageHeader eyebrow="Sales & Leads" title="Sales Dashboard" desc={`New leads ${d.range.from} → ${d.range.to}, shown separately from activity on existing leads. Totals cover only the records you're allowed to see.`} />
      <HotLeads />
      <Filters f={f} setF={setF} staff={staff} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
        <Kpi testid="kpi-new-leads" label="New leads generated" value={d.new_leads} hint="Created in selected range" tone="#ff5722" />
        <Kpi testid="kpi-existing-worked" label="Existing leads worked" value={d.existing_worked} hint="Older leads contacted in range" tone="#0ea5e9" />
        <Kpi testid="kpi-open" label="Open pipeline (A–E)" value={fu.open || 0} hint="Not won or lost" onClick={() => nav("/sales/leads?status=A,B,C,D,E")} />
        <Kpi testid="kpi-dup" label="Duplicates to review" value={d.dup_pending} onClick={() => nav("/sales/duplicates")} tone="#b26a00" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Kpi testid="kpi-due-today" label="Follow-ups due today" value={fu.due_today || 0} onClick={() => nav("/sales/followups?tab=today")} />
        <Kpi testid="kpi-overdue" label="Missed follow-ups" value={fu.overdue || 0} tone="#f43f5e" onClick={() => nav("/sales/followups?tab=overdue")} />
        <Kpi testid="kpi-no-next" label="Open, no next action" value={fu.no_next_action || 0} tone="#b26a00" onClick={() => nav("/sales/followups?tab=none")} />
        <Kpi testid="kpi-stale" label={`Inactive > ${14} days`} value={fu.stale || 0} hint="Open leads not contacted recently" onClick={() => nav("/sales/leads?stale=1")} />
      </div>
      <div className="grid lg:grid-cols-12 gap-4 mb-4">
        <Panel title="Monthly lead generation (last 12 months)" className="lg:col-span-7" testid="chart-monthly">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={monthly}>
              <CartesianGrid stroke="#e8ecf3" vertical={false} /><XAxis dataKey="m" tick={TICK} /><YAxis tick={TICK} />
              <Tooltip {...tip} /><Bar dataKey="n" name="Leads" fill="#ff5722" /><Bar dataKey="won" name="Won" fill="#10b981" />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
        <Panel title="Pipeline by status (all accessible leads)" className="lg:col-span-5" testid="chart-funnel">
          <div className="space-y-2">
            {funnel.map((s) => {
              const max = Math.max(...funnel.map((x) => x.n), 1);
              return (
                <button key={s.code} onClick={() => nav(`/sales/leads?status=${s.code}`)} className="w-full text-left group" data-testid={`funnel-${s.code}`}>
                  <div className="flex justify-between text-xs mb-1"><span>{s.name}</span><span className="num">{s.n}</span></div>
                  <div className="h-2 bg-muted"><div className="h-2 transition-all group-hover:opacity-80" style={{ width: `${(s.n / max) * 100}%`, background: STATUS_COLORS[s.code] }} /></div>
                </button>
              );
            })}
          </div>
        </Panel>
      </div>
      <div className="grid lg:grid-cols-12 gap-4 mb-4">
        <Panel title="New leads by source (selected range)" className="lg:col-span-5" testid="chart-new-source">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={bySource} layout="vertical" margin={SOURCE_MARGIN}>
              <XAxis type="number" tick={TICK} /><YAxis type="category" dataKey="s" tick={TICK} width={90} />
              <Tooltip {...tip} /><Bar dataKey="n" name="New leads">{d.new_by_source.map((s, i) => <Cell key={s._id || "(none)"} fill={i === 0 ? "#ff5722" : "#0ea5e9"} />)}</Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>
        <Panel title="Source-wise conversion (all time)" className="lg:col-span-7 overflow-x-auto" testid="table-source-conversion">
          <table className="ax-table w-full"><thead><tr><th>Source</th><th>Leads</th><th>Qualified</th><th>RFQ+</th><th>Won</th><th>Lost</th><th>Win %</th></tr></thead>
            <tbody>{d.source_conversion.map((s) => (
              <tr key={s._id || "none"}><td>{s._id || "(none)"}</td><td className="num">{s.total}</td><td className="num">{s.qualified}</td><td className="num">{s.rfq}</td>
                <td className="num text-emerald-400">{s.won}</td><td className="num text-rose-400">{s.lost}</td><td className="num">{s.total ? ((s.won / s.total) * 100).toFixed(1) : 0}%</td></tr>
            ))}</tbody></table>
        </Panel>
      </div>
      <div className="grid lg:grid-cols-12 gap-4">
        <Panel title="Owner accountability" className="lg:col-span-8 overflow-x-auto" testid="table-owners">
          <table className="ax-table w-full"><thead><tr><th>Owner</th><th>Total</th><th>New in range</th><th>Open</th><th>Missed follow-ups</th><th>Won</th><th>Lost</th></tr></thead>
            <tbody>{d.owners.map((o) => (
              <tr key={o._id || "none"} className="cursor-pointer" onClick={() => setF({ ...f, owner: o._id || "" })}>
                <td>{o._id || "(unassigned)"}</td><td className="num">{o.total}</td><td className="num">{o.new_in_range}</td><td className="num">{o.open}</td>
                <td className={`num ${o.overdue ? "text-rose-400" : ""}`}>{o.overdue}</td><td className="num">{o.won}</td><td className="num">{o.lost}</td></tr>
            ))}</tbody></table>
        </Panel>
        <Panel title="New vs repeat · segment · branch" className="lg:col-span-4" testid="panel-mix">
          {[["Customer type", d.new_vs_repeat, {}], ["Segment", d.by_type, {}], ["Branch", d.by_branch, BRANCH_LABEL]].map(([t, rows, names]) => (
            <div key={t} className="mb-4"><div className="text-xs text-muted-foreground mb-1">{t}</div>
              {rows.map((r) => <div key={String(r._id)} className="flex justify-between text-sm py-0.5"><span>{names[r._id] || r._id || "(blank)"}</span><span className="num">{r.n}</span></div>)}</div>
          ))}
        </Panel>
      </div>
      <p className="text-xs text-muted-foreground mt-4">Status meanings follow your Sales Tracker. {lbl.D} is the RFQ/Quote stage. RFQ+ counts leads at D, E or F.</p>
    </div>
  );
}
